import { openDB, type IDBPDatabase } from 'idb'

export interface OfflineMutation {
  id: string
  endpoint: string
  method: 'POST' | 'PATCH' | 'DELETE'
  data: any
  headers: Record<string, string>
  timestamp: number
  retryCount: number
  maxRetries: number
  status: 'pending' | 'processing' | 'completed' | 'failed'
  errorMessage?: string
  protocolId?: number
  mutationType:
    | 'protocol'
    | 'furniture'
    | 'animal'
    | 'key'
    | 'meter'
    | 'room'
    | 'item'
    | 'sign'
    | 'protocolHouseholdAppliances'
    | 'protocolFurnitures'
}

export interface QueueStats {
  pending: number
  processing: number
  failed: number
  total: number
}

class OfflineMutationQueue {
  private db: IDBPDatabase | null = null
  private readonly dbName = 'resi4rent-offline-mutations'
  private readonly dbVersion = 1
  private readonly storeName = 'mutations'

  async init(): Promise<void> {
    if (this.db) return

    this.db = await openDB(this.dbName, this.dbVersion, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('mutations')) {
          const store = db.createObjectStore('mutations', { keyPath: 'id' })
          store.createIndex('status', 'status')
          store.createIndex('timestamp', 'timestamp')
          store.createIndex('protocolId', 'protocolId')
          store.createIndex('mutationType', 'mutationType')
        }
      }
    })
  }

  async queueMutation(
    mutation: Omit<OfflineMutation, 'id' | 'timestamp' | 'status' | 'retryCount'>
  ): Promise<string> {
    await this.init()

    const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const fullMutation: OfflineMutation = {
      ...mutation,
      id,
      timestamp: Date.now(),
      status: 'pending',
      retryCount: 0
    }

    await this.db!.add(this.storeName, fullMutation)
    console.log('Queued offline mutation:', id, mutation.mutationType)

    return id
  }

  async getPendingMutations(): Promise<OfflineMutation[]> {
    await this.init()
    return this.db!.getAllFromIndex(this.storeName, 'status', 'pending')
  }

  async getQueueStats(): Promise<QueueStats> {
    await this.init()
    const all = await this.db!.getAll(this.storeName)

    const stats: QueueStats = {
      pending: 0,
      processing: 0,
      failed: 0,
      total: all.length
    }

    all.forEach((mutation) => {
      switch (mutation.status) {
        case 'pending':
          stats.pending++
          break
        case 'processing':
          stats.processing++
          break
        case 'failed':
          stats.failed++
          break
      }
    })

    return stats
  }

  async updateMutationStatus(
    id: string,
    status: OfflineMutation['status'],
    errorMessage?: string
  ): Promise<void> {
    await this.init()
    const mutation = await this.db!.get(this.storeName, id)

    if (mutation) {
      mutation.status = status
      if (errorMessage) {
        mutation.errorMessage = errorMessage
      }
      if (status === 'failed') {
        mutation.retryCount++
      }

      await this.db!.put(this.storeName, mutation)
    }
  }

  async removeMutation(id: string): Promise<void> {
    await this.init()
    await this.db!.delete(this.storeName, id)
  }

  async clearCompleted(): Promise<void> {
    await this.init()
    const completed = await this.db!.getAllFromIndex(this.storeName, 'status', 'completed')

    for (const mutation of completed) {
      await this.db!.delete(this.storeName, mutation.id)
    }
  }

  async clearAll(): Promise<void> {
    await this.init()
    await this.db!.clear(this.storeName)
  }

  async getMutationsByProtocol(protocolId: number): Promise<OfflineMutation[]> {
    await this.init()
    return this.db!.getAllFromIndex(this.storeName, 'protocolId', protocolId)
  }

  async getFailedMutations(): Promise<OfflineMutation[]> {
    await this.init()
    return this.db!.getAllFromIndex(this.storeName, 'status', 'failed')
  }

  async retryFailedMutation(id: string): Promise<void> {
    await this.init()
    const mutation = await this.db!.get(this.storeName, id)

    if (mutation && mutation.status === 'failed') {
      mutation.status = 'pending'
      mutation.errorMessage = undefined
      await this.db!.put(this.storeName, mutation)
    }
  }

  // Helper method to serialize FormData for storage
  static async serializeFormData(formData: FormData): Promise<any> {
    const serialized: Record<string, any> = {}

    for (const [key, value] of formData.entries()) {
      if (value instanceof File) {
        // Convert File to base64 for storage
        const base64 = await new Promise<string>((resolve) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result as string)
          reader.readAsDataURL(value)
        })

        serialized[key] = {
          type: 'file',
          name: value.name,
          size: value.size,
          mimeType: value.type,
          data: base64
        }
      } else {
        serialized[key] = value
      }
    }

    return {
      type: 'formData',
      data: serialized
    }
  }

  // Helper method to deserialize FormData from storage
  static deserializeFormData(serialized: any): FormData {
    const formData = new FormData()

    if (serialized.type === 'formData') {
      Object.entries(serialized.data).forEach(([key, value]: [string, any]) => {
        if (value && typeof value === 'object' && value.type === 'file') {
          // Convert base64 back to File
          const byteCharacters = atob(value.data.split(',')[1])
          const byteNumbers = new Array(byteCharacters.length)

          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i)
          }

          const byteArray = new Uint8Array(byteNumbers)
          const file = new File([byteArray], value.name, { type: value.mimeType })
          formData.append(key, file)
        } else {
          formData.append(key, value)
        }
      })
    }

    return formData
  }
}

// Export both the class and singleton instance
export { OfflineMutationQueue }
export const mutationQueue = new OfflineMutationQueue()
